import { useEffect, useState } from "react";
import { trackEvent } from "../../lib/analytics";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

const FORMAT_LABELS = {
  fixed_kits: "Fixed Kits",
  mix_match: "Mix & Match",
  byob: "Build-Your-Own",
  upsells: "Upsell layer",
};

const FORMAT_EMOJI = {
  fixed_kits: "🍕",
  mix_match: "🧀",
  byob: "🥡",
  upsells: "🥤",
};

/* Menu-speak for the audit's tri-state coverage. Server sends
   missing/partial/present; the kitchen says missing/prepping/ready. */
const STATUS_TAGS = {
  missing: { label: "missing", className: "tag missing" },
  partial: { label: "prepping", className: "tag partial" },
  present: { label: "ready", className: "tag live" },
};

const FORMAT_PITCHES = {
  fixed_kits: "The easiest BFCM yes — one price, one add to cart.",
  mix_match: "Turns one discount into a bigger basket.",
  byob: "One builder covers everyone on a gift list.",
  upsells: "Something to offer after the first click.",
};

/* Our own playbook, same six moves as the standard readiness page. */
const PLAYBOOK = [
  { title: "Launch by early October", body: "Bundles need live sales data to tune contents and pricing. Launch early and your BFCM ads point at offers already proven." },
  { title: "Anchor sets to your bestseller", body: "Your highest-traffic product page is your best bundle billboard. Build the first gift set around the product people already come for." },
  { title: "Ladder your price points", body: "Gift shoppers arrive with a budget in mind. A good, better, best spread gives $25, $50 and $100 each somewhere to land." },
  { title: "Show the savings", body: "A compare-at price on the bundle does the discount math for the shopper. Visible savings is what makes a set read as a deal." },
  { title: "Sync inventory to components", body: "Bundles that oversell components become the January refund pile. Component-level tracking is why bundle sellers see 80% fewer fulfillment issues." },
  { title: "Give bundles a home", body: "A dedicated gift or bundles collection gives every ad, email and influencer link one obvious place to land." },
];

/* The pizza-slice progress dials on the playbook cards: n filled sixths. */
function sliceDial(filled) {
  const wedges = Array.from({ length: 6 }, (_, i) => {
    const color = i < filled ? (i % 2 === 0 ? "var(--cheese)" : "#F3C635") : "var(--paper-alt)";
    return `${color} ${i * 60}deg ${(i + 1) * 60}deg`;
  }).join(", ");
  return {
    backgroundImage: `repeating-conic-gradient(var(--ink) 0deg 2deg, transparent 2deg 60deg), conic-gradient(from 0deg, ${wedges})`,
  };
}

const PRIZE_SCORE = 90;

const money = (currency, value) => `${currency}${Number(value ?? 0).toFixed(2)}`;
const moneyRange = (currency, low, high) =>
  Number(low) === Number(high) ? money(currency, low) : `${money(currency, low)}–${money(currency, high)}`;

/* Menu category per bundle STI type; anything unexpected lands in Fixed. */
const CATEGORY_ORDER = [
  { type: "SimpleBundle", title: "Fixed Bundles", blurb: "One price, one add to cart — the easiest BFCM yes." },
  { type: "InfiniteOptionsBundle", title: "Mix & Match", blurb: "Sets your customers personalize, straight from your catalog." },
  { type: "ByobBundle", title: "Build Your Own", blurb: "A bundle builder, with your products in it." },
];

/* The bundle menu result page: the same teaser/report payloads as the
   standard readiness report, plated as a pizzeria menu. Free half: score
   on and inside the pizza box, the tab, coverage, playbook. Behind the
   email gate: the real bundles as today's tasting menu. The locked content
   never reaches the browser before the unlock round-trip. */
export default function PizzaMenuResults({ teaser, report: initialReport, claimUrl, supportUrl, teamTablePhoto, scoreAnotherPath }) {
  const [report, setReport] = useState(initialReport);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const unlocked = Boolean(report);
  const bfcm = teaser.bfcm || {};

  useEffect(() => {
    if (justUnlocked) document.getElementById("tasting-menu")?.scrollIntoView({ behavior: "smooth" });
  }, [justUnlocked]);

  useEffect(() => {
    document.body.classList.add("has-sticky-cta");
    return () => document.body.classList.remove("has-sticky-cta");
  }, []);

  return (
    <>
      <OrderHero teaser={teaser} unlocked={unlocked} />
      <KitchenBand clock={bfcm.clock} />
      <TheTab gap={teaser.gap} currency={teaser.currencySymbol} />
      <CoverageBoard teaser={teaser} report={report} />
      <Playbook />
      {unlocked && <TastingMenu report={report} teaser={teaser} />}
      <PrizePanel score={teaser.score} />
      {unlocked ? (
        <ClosingCta teaser={teaser} report={report} claimUrl={claimUrl} supportUrl={supportUrl} teamTablePhoto={teamTablePhoto} />
      ) : (
        <UnlockGate teaser={teaser} teamTablePhoto={teamTablePhoto} onUnlocked={(next) => { setReport(next); setJustUnlocked(true); }} />
      )}
      <p className="taste-more">
        Scored from public storefront data · <a href={scoreAnotherPath}>Score another store</a>
      </p>
      <StickyCta teaser={teaser} unlocked={unlocked} claimUrl={claimUrl} />
    </>
  );
}

/* ---------- The order: copy beside the box, grade readable at a glance ---------- */

function OrderHero({ teaser, unlocked }) {
  const [open, setOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const prizeReached = Number(teaser.score) >= PRIZE_SCORE;
  const statuses = teaser.formatStatuses || [];

  /* The lid shows grade + score and the interior repeats them, so nothing
     vital hides behind the tap — but open the box once on arrival so the
     payoff (and the why) isn't left to curiosity. The delay lets the
     grade-on-the-lid moment land first. Skipped for reduced-motion users. */
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setTimeout(() => { setOpen(true); setAutoOpened(true); }, 2200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="pz-hero">
      <div className="pz-wrap">
        <div className="pz-hero-grid">
          <div>
            <h1 className="order-ready">Your order is ready</h1>
            <h2 className="headline">{teaser.headline}</h2>
            <p className="sub">{teaser.subheadline}</p>
            <div className="ticket-chips">
              <span className="ticket-chip">🧾 {Number(teaser.productsCount || 0).toLocaleString()} products read</span>
              {teaser.collectionsCount > 0 && <span className="ticket-chip">📚 {teaser.collectionsCount} collections</span>}
              {teaser.gap && (
                <span className="ticket-chip">
                  💸 {moneyRange(teaser.currencySymbol, teaser.gap.gap_low, teaser.gap.gap_high)} left on the table per order
                </span>
              )}
            </div>
            <div className="hero-ctas">
              {unlocked
                ? <a className="btn tomato" href="#tasting-menu">See my bundle menu ↓</a>
                : <a className="btn tomato" href="#claim">Unlock my bundle menu ↓</a>}
              <a className="btn white" href="#howto">How to score a {PRIZE_SCORE}</a>
            </div>
          </div>

          <div className="pizza-box-giant">
            <div
              className={`pizza-box${open ? " open" : ""}`}
              onClick={() => setOpen(!open)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}
              aria-expanded={open}
              aria-label={`Readiness grade ${teaser.grade}, score ${teaser.score} out of 100. Activate to ${open ? "close" : "open"} the box.`}
            >
              <div className="box-wall"></div>
              <div className="box-base">
                <div className="interior-content">
                  <span className="interior-score">Grade {teaser.grade} · {teaser.score} / 100</span>
                  <span className="kicker3">{prizeReached ? `you hit ${PRIZE_SCORE}+ 🎉` : `why you scored a ${teaser.grade}`}</span>
                  <ul className="chef-menu-list">
                    {statuses.map((f) => {
                      const tag = STATUS_TAGS[f.status] || STATUS_TAGS.missing;
                      return (
                        <li key={f.key}>
                          <span className={tag.className}>{tag.label}</span>
                          <span><b>{FORMAT_LABELS[f.key] || f.key}</b><span className="pitch"> — {FORMAT_PITCHES[f.key] || ""}</span></span>
                        </li>
                      );
                    })}
                  </ul>
                  {prizeReached ? (
                    <a className="btn tomato sm" href="#prize" onClick={(e) => e.stopPropagation()}>Claim your surprise →</a>
                  ) : (
                    <span className="ninety-note">🍕 Score {PRIZE_SCORE}+ before BFCM and unlock a surprise from our team.</span>
                  )}
                </div>
              </div>
              <div className="box-lid">
                <div className="vent-row" aria-hidden="true"><span></span><span></span><span></span></div>
                <div className="grease-spot" aria-hidden="true"></div>
                <div className="retro-badge">
                  <span className="retro-letter">{teaser.grade}</span>
                  <span className="retro-score">{teaser.score} / 100</span>
                </div>
                <span className={`tap-hint-inside${autoOpened ? "" : " pulse"}`}>{open ? "tap to close" : "tap to see your pizza"}</span>
                <div className="box-tab"><span className="tab-notch"></span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- The Q4 clock, kitchen-flavored ---------- */

function KitchenBand({ clock }) {
  if (!clock) return null;
  return (
    <div className="kitchen-band">
      <div className="pz-wrap">
        <div className="left">
          <span className="kicker2">the kitchen closes soon</span>
          <h3>{clock.headline}</h3>
          <p>{clock.body}</p>
        </div>
        {clock.statValue != null && (
          <div className="days-box"><div className="n">{clock.statValue}</div><div className="l">{clock.statLabel}</div></div>
        )}
      </div>
    </div>
  );
}

/* ---------- The tab so far (gap bars) ---------- */

function TheTab({ gap, currency }) {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGrown(true), 350);
    return () => clearTimeout(t);
  }, []);
  if (!gap) return null;

  const max = gap.bundled_high || 1;
  const rows = [
    { label: "A typical order today", low: gap.order_value_low, high: gap.order_value_high, cls: "today" },
    { label: "The same order, bundled", low: gap.bundled_low, high: gap.bundled_high, cls: "bundled" },
  ];

  return (
    <section className="menu-section tight">
      <div className="pz-wrap">
        <div className="section-head">
          <span className="kicker">the tab so far</span>
          <h2>Your average order value could be a lot bigger.</h2>
        </div>
        <div className="bar-card">
          {rows.map((row) => (
            <div className="bar-row" key={row.label}>
              <div className="bar-label"><span>{row.label}</span><span className="amount">{moneyRange(currency, row.low, row.high)}</span></div>
              <div className="bar-track">
                <div className={`bar-fill ${row.cls}`} style={{ width: grown ? `${(row.high / max) * 100}%` : "0%" }} />
              </div>
            </div>
          ))}
          <p className="bar-note">
            Order value proxied from your public catalog prices; the lift is the published average across 25,000+ Simple Bundles merchants, applied to your own prices.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- Coverage: what's on the menu board today ---------- */

function CoverageBoard({ teaser, report }) {
  const statuses = report?.formatFindings || teaser.formatStatuses || [];

  return (
    <section className="menu-section alt">
      <div className="pz-wrap">
        <div className="section-head center">
          <span className="kicker">the menu board</span>
          <h2>BFCM shoppers buy in multiples. Here's your coverage.</h2>
        </div>
        <div className="coverage-grid">
          {statuses.map((f) => {
            const tag = STATUS_TAGS[f.status] || STATUS_TAGS.missing;
            return (
              <div className="coverage-card" key={f.key}>
                <div className="top-row">
                  <div className="format-name">
                    <span className="emoji" aria-hidden="true">{FORMAT_EMOJI[f.key] || "🍽️"}</span>
                    <h4>{FORMAT_LABELS[f.key] || f.key}</h4>
                  </div>
                  <span className={tag.className}>{tag.label}</span>
                </div>
                <p>{f.insight || FORMAT_PITCHES[f.key] || ""}</p>
                {!f.insight && <p className="angle">Unlock your bundle menu below for the full read on this.</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- How to get a 90 ---------- */

function Playbook() {
  return (
    <section className="menu-section" id="howto">
      <div className="pz-wrap">
        <div className="section-head center">
          <span className="kicker">how to get a {PRIZE_SCORE}</span>
          <h2>Six moves the ready stores make.</h2>
          <p>Shoppers buy in multiples this time of year — gifts, stock-ups, treat-yourself carts. Here's the playbook to catch them.</p>
        </div>
        <div className="step-grid">
          {PLAYBOOK.map((step, index) => (
            <div className="step-card" key={step.title}>
              <div className="pizza-progress" style={sliceDial(index + 1)} aria-hidden="true"></div>
              <h4>{step.title}</h4>
              <p>{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Behind the gate: today's tasting menu (real bundles) ---------- */

function TastingMenu({ report, teaser }) {
  const bundles = report.bundles || [];
  if (!bundles.length) return null;

  const categories = CATEGORY_ORDER
    .map((c) => ({ ...c, bundles: bundles.filter((b) => b.type === c.type) }))
    .filter((c) => c.bundles.length);
  const uncategorized = bundles.filter((b) => !CATEGORY_ORDER.some((c) => c.type === b.type));
  if (uncategorized.length) categories.push({ type: "other", title: "Chef's Specials", blurb: "More sets built from your catalog.", bundles: uncategorized });

  return (
    <section className="menu-section alt" id="tasting-menu">
      <div className="pz-wrap">
        <div className="section-head center">
          <span className="kicker">today's tasting menu</span>
          <h2>We already built your BFCM lineup.</h2>
          <p>Not mockups — every set below is designed from {teaser.storeName}'s real products and ready to sell.</p>
        </div>
        {categories.map((category, index) => (
          <div className="category-block" key={category.type}>
            <div className="category-label">
              <div className="num-badge">{index + 1}</div>
              <div>
                <h3>{category.title}</h3>
                <p>{category.blurb}</p>
              </div>
            </div>
            <div className="menu-grid">
              {category.bundles.map((bundle) => (
                <BundleCard key={bundle.id} bundle={bundle} currency={teaser.currencySymbol} />
              ))}
            </div>
          </div>
        ))}
        {report.demoPath && (
          <p className="taste-more">
            <a className="btn white" href={report.demoPath} target="_blank" rel="noopener">Open your full interactive demo →</a>
          </p>
        )}
      </div>
    </section>
  );
}

function BundleCard({ bundle, currency }) {
  const photos = (bundle.items || []).map((item) => item.image_url).filter(Boolean).slice(0, 2);
  const components = (bundle.items || []).slice(0, 3);
  const hasSavings = bundle.compare_at_price && bundle.price && bundle.compare_at_price > bundle.price;

  return (
    <div className="menu-card">
      <div className={`photo-strip${photos.length === 1 ? " single" : ""}`}>
        {photos.length
          ? photos.map((url) => <img key={url} src={url} alt="" loading="lazy" />)
          : <div className="ph" aria-hidden="true">🎁</div>}
        {hasSavings && bundle.discount_percentage > 0 && <span className="save-badge">Save {Math.round(bundle.discount_percentage)}%</span>}
      </div>
      <div className="card-body">
        <div className="type-chip">{bundle.human_type}</div>
        <h4>{bundle.title}</h4>
        {bundle.tagline && <p className="tagline">{bundle.tagline}</p>}
        {components.length > 0 && (
          <ul className="comp-list">
            {components.map((item) => (
              <li key={item.id}><span>{item.title}{item.quantity > 1 ? ` ×${item.quantity}` : ""}</span><span>{money(currency, item.price)}</span></li>
            ))}
            {bundle.item_count > components.length && <li><span>+ {bundle.item_count - components.length} more</span><span></span></li>}
          </ul>
        )}
        <div className="price-row">
          {bundle.price != null && <span className="total">{money(currency, bundle.price)}</span>}
          {hasSavings && <span className="was">{money(currency, bundle.compare_at_price)}</span>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Today's special: the 90+ prize ---------- */

function PrizePanel({ score }) {
  const reached = Number(score) >= PRIZE_SCORE;
  const progress = Math.min(100, Math.round((Number(score) / PRIZE_SCORE) * 100));

  return (
    <section className="menu-section tight" id="prize">
      <div className="pz-wrap">
        <div className={`special-panel${reached ? " unlocked" : ""}`}>
          <div className="special-grid">
            <div className="box-stage" aria-hidden="true">
              <div className="mini-base">🎁</div>
              <div className="mini-lid"><span className="lid-icon">🍕</span></div>
            </div>
            <div className="special-copy">
              <span className="kicker3">today's special</span>
              <h3>{reached ? "You hit the passing grade. Dinner's on us." : `Score ${PRIZE_SCORE}+ before BFCM and unlock a surprise.`}</h3>
              <p>
                {reached
                  ? "Your bundle lineup is BFCM ready — claim your surprise below and our team will take it from there."
                  : `You're at ${score} / 100. Get your bundle formats live before the season and re-score this store — hit ${PRIZE_SCORE} and there's a delicious thank-you from our team waiting.`}
              </p>
              {reached ? (
                <div className="code-chip"><div className="lbl">your surprise</div><div className="amt">🎁 ???</div></div>
              ) : (
                <>
                  <div className="progress-track"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
                  <span className="progress-label">{score} / {PRIZE_SCORE} to the prize</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- The gate / the closing table ---------- */

/* Three blurred stand-in cards so the gate reads as "the menu is right
   there"; the real bundle data never reaches the browser until unlock. */
function LockedPreview() {
  const swatches = ["var(--sky)", "var(--pink)", "var(--cheese)"];
  return (
    <div className="locked-preview" aria-hidden="true">
      <div className="menu-grid">
        {swatches.map((bg) => (
          <div className="locked-card" key={bg}>
            <div className="swatch" style={{ background: bg }}>🎁</div>
            <div className="body">
              <div className="locked-line short"></div>
              <div className="locked-line"></div>
              <div className="locked-line"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UnlockGate({ teaser, teamTablePhoto, onUnlocked }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(teaser.unlockPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        trackEvent("generate_lead", { form: "bundle_menu_unlock" });
        onUnlocked(data.report);
      } else {
        setError(Object.values(data.errors || {}).flat().join(" ") || "Something went wrong. Please try again.");
        setSubmitting(false);
      }
    } catch {
      setError("We couldn't reach the server. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <section className="menu-section alt" id="claim">
      <div className="pz-wrap">
        <div className="section-head center">
          <span className="kicker">today's tasting menu</span>
          <h2>Your bundle menu is plated and under the heat lamp.</h2>
        </div>
        <LockedPreview />
        <div className="final-cta-grid">
          <div className="claim-card">
            <span className="kicker">order up</span>
            <h3>We already built {teaser.storeName}'s BFCM bundle menu.</h3>
            <ul className="claim-list">
              <li><span aria-hidden="true">🍕</span> Ready-made bundles, built from your real products</li>
              <li><span aria-hidden="true">🧾</span> The full read on each gap in your readiness score</li>
              <li><span aria-hidden="true">🔓</span> This link stays unlocked for your whole team</li>
            </ul>
            <form className="claim-form" onSubmit={submit} noValidate>
              <input
                type="email"
                required
                placeholder="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Work email"
              />
              <button className="btn tomato" type="submit" disabled={submitting}>
                {submitting ? "Unlocking…" : "Show me my menu →"}
              </button>
            </form>
            {error && <p className="form-error">{error}</p>}
            <p className="intake-fine" style={{ marginTop: "14px" }}>Free · No app install</p>
          </div>
          <TeamTable teamTablePhoto={teamTablePhoto} />
        </div>
      </div>
    </section>
  );
}

function ClosingCta({ teaser, report, claimUrl, supportUrl, teamTablePhoto }) {
  return (
    <section className="menu-section" id="claim">
      <div className="pz-wrap">
        <div className="final-cta-grid">
          <div className="claim-card">
            <span className="kicker">your bfcm bundles are ready</span>
            <h3>Now let's get this menu live on {teaser.storeName}.</h3>
            {report.closing && <p className="lead">{report.closing}</p>}
            <a className="btn tomato" href={claimUrl} target="_blank" rel="noopener">Claim my bundles →</a>
            <div className="claim-divider"><span className="line"></span>or<span className="line"></span></div>
            <a className="btn white" href={supportUrl} target="_blank" rel="noopener">Talk to our team</a>
          </div>
          <TeamTable teamTablePhoto={teamTablePhoto} />
        </div>
      </div>
    </section>
  );
}

function TeamTable({ teamTablePhoto }) {
  if (!teamTablePhoto) return null;
  return (
    <div className="team-cta-side">
      <div className="team-photo-frame">
        <img src={teamTablePhoto} alt="The Simple Bundles team sitting around a table eating pizza together" loading="lazy" />
      </div>
      <p className="table-closing">Take a seat at the Simple Bundles table this BFCM</p>
    </div>
  );
}

/* ---------- Mobile: the order never scrolls away ---------- */

function StickyCta({ teaser, unlocked, claimUrl }) {
  return (
    <div className="sticky-cta">
      <div className="score-mini">
        <span className="g">{teaser.grade}</span>
        <span className="s">{teaser.score} / 100</span>
      </div>
      {unlocked
        ? <a className="btn tomato sm" href={claimUrl} target="_blank" rel="noopener">Claim my bundles →</a>
        : <a className="btn tomato sm" href="#claim">Unlock my menu →</a>}
    </div>
  );
}
